/**
 * The words and small rules of the Traces tab's Logs and Metrics views (LogsView.jsx, MetricsView.jsx): the views of the
 * tab, the severity filter and pills, a log row's time, a metric's value in words and its line chart's geometry.
 * Pure, tested in signalsModel.test.js.
 */

/** The tab's views: the service map with the requests, the services' log records, their metrics. */
export const VIEWS = [
  { id: 'requests', label: 'Requests' },
  { id: 'logs', label: 'Logs' },
  { id: 'metrics', label: 'Metrics' },
];

/** The severity filter: that level and above (vfs-service's `severity`). */
export const SEVERITIES = [
  { value: '', label: 'All levels' },
  { value: 'debug', label: 'Debug and above' },
  { value: 'info', label: 'Info and above' },
  { value: 'warn', label: 'Warnings and errors' },
  { value: 'error', label: 'Errors only' },
];

export const LOGS_LIMIT = 100;

/** A severity's pill: `{ text, tone }` (tone: 'bad' for error and fatal, 'warn' for warn, else null). */
export function severityPill(severity) {
  const value = String(severity || 'info').toLowerCase();
  if (value === 'error' || value === 'fatal') { return { text: value, tone: 'bad' }; }
  if (value === 'warn') { return { text: 'warn', tone: 'warn' }; }
  return { text: value, tone: null };
}

/** "14:03:07.215" (today) or "Oct 29 14:03:07" (an earlier day): a log row's time. */
export function logTime(ms, now = Date.now()) {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) { return ''; }
  const sameDay = new Date(now).toDateString() === date.toDateString();
  const hms = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  if (sameDay) { return `${hms}.${String(date.getMilliseconds()).padStart(3, '0')}`; }
  return `${date.toLocaleDateString('en', { month: 'short', day: 'numeric' })} ${hms}`;
}

/** A log record's attributes as one short line ("http.route=/orders · order.id=42"), at most `max` of them. */
export function attributesLine(attributes, max = 4) {
  const entries = Object.entries(attributes || {});
  const shown = entries.slice(0, max).map(([key, value]) => `${key}=${typeof value === 'object' ? JSON.stringify(value) : value}`);
  return entries.length > max ? `${shown.join(' · ')} · +${entries.length - max}` : shown.join(' · ');
}

const UNIT_WORDS = { s: 's', ms: 'ms', By: 'bytes', '1': '', '%': '%' };

/** A unit as people read it: "s", "ms", "bytes", "orders" for "{order}" (UCUM annotations), "" for "1". */
export function unitText(unit) {
  const value = String(unit || '');
  if (value in UNIT_WORDS) { return UNIT_WORDS[value]; }
  const annotation = /^\{(.+)\}$/.exec(value);
  if (annotation) { return `${annotation[1]}s`; }
  return value;
}

/** A value with its unit: "0.25 s", "1,204 bytes", "3 orders", "12.5". Seconds under 1 read as ms. */
export function formatValue(value, unit) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) { return '–'; }
  let n = Number(value);
  let words = unitText(unit);
  if (words === 's' && Math.abs(n) < 1 && n !== 0) {
    n *= 1000;
    words = 'ms';
  }
  const digits = Math.abs(n) >= 100 ? 0 : Math.abs(n) >= 1 ? 2 : 3;
  const text = n.toLocaleString('en', { maximumFractionDigits: digits });
  return words ? `${text} ${words}` : text;
}

/** What a series' values are, for the chart's title: "Value", "Increase between exports", "Average between exports". */
export function valueIsText(valueIs) {
  if (valueIs === 'increase') { return 'Increase between exports'; }
  if (valueIs === 'average') { return 'Average between exports'; }
  return 'Value';
}

/** "orders" or "orders · http.route=/orders": which series a line is. */
export function seriesLabel(series) {
  const attrs = attributesLine(series?.attributes, 3);
  return attrs ? `${series?.service} · ${attrs}` : String(series?.service || '');
}

/** A metric's kind in words: "Counter", "Gauge", "Up-down counter", "Histogram", "Summary". */
export function kindText(metric) {
  if (metric?.kind === 'sum') { return metric.monotonic ? 'Counter' : 'Up-down counter'; }
  return { gauge: 'Gauge', histogram: 'Histogram', summary: 'Summary' }[metric?.kind] || 'Metric';
}

/**
 * The chart's geometry in a `width` × `height` box: `{ path, dots, min, max, from, to }` (path: an SVG polyline's
 * points, x by time between `from` and `to`, y from min to max; a flat line sits in the middle). Empty: path ''.
 */
export function lineGeometry(points, { width, height, from = null, to = null, pad = 4 } = {}) {
  const list = (points || []).filter((p) => Number.isFinite(p?.value) && Number.isFinite(p?.t));
  if (!list.length) { return { path: '', dots: [], min: null, max: null, from, to }; }
  const start = from ?? list[0].t;
  const end = to ?? list[list.length - 1].t;
  const values = list.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = end - start || 1;
  const x = (t) => pad + ((t - start) / span) * (width - pad * 2);
  const y = (v) => (max === min ? height / 2 : pad + (1 - (v - min) / (max - min)) * (height - pad * 2));
  const dots = list.map((p) => ({ x: Math.round(x(p.t) * 10) / 10, y: Math.round(y(p.value) * 10) / 10, t: p.t, value: p.value }));
  return { path: dots.map((d) => `${d.x},${d.y}`).join(' '), dots, min, max, from: start, to: end };
}

/** The chart in words, for a screen reader: "queue.depth for orders over the last hour: 12 points, from 2 to 9, last 7". */
export function chartWords(name, series, rangeLabel) {
  const s = series?.summary || {};
  const unit = series?.unit;
  if (!s.count) { return `${name} for ${seriesLabel(series)}: no points ${String(rangeLabel || '').toLowerCase()}`; }
  return `${name} for ${seriesLabel(series)} ${String(rangeLabel || '').toLowerCase()}: ${s.count} point${s.count === 1 ? '' : 's'}, from ${formatValue(s.min, unit)} to ${formatValue(s.max, unit)}, last ${formatValue(s.last, unit)}`;
}
